import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  firstName: z.string().optional(),
  lastName: z.string().optional()
});

export type EmailCaptureValues = z.infer<typeof schema>;

interface Props {
  onSubmit: (values: EmailCaptureValues) => Promise<unknown>;
  isSubmitting?: boolean;
}

const EmailCaptureForm = ({ onSubmit, isSubmitting }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<EmailCaptureValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: ''
    }
  });

  return (
    <form
      onSubmit={handleSubmit(async (values) => onSubmit(values))}
      className="theme-card p-8 text-left bg-white"
    >
      <div className="flex flex-col gap-2 text-center">
        <h2 className="text-3xl font-semibold text-slate-900">One more thing</h2>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-2 sm:col-span-2 text-left">
          <span className="text-sm font-medium text-slate-700">Work email</span>
          <input
            type="email"
            {...register('email')}
            className={clsx(
              'rounded-xl border bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]',
              errors.email ? 'border-red-500' : 'border-slate-300'
            )}
            placeholder="you@business.com"
            autoComplete="email"
            required
          />
          {errors.email && <span className="text-sm text-red-400">{errors.email.message}</span>}
        </label>
        <label className="flex flex-col gap-2 text-left">
          <span className="text-sm font-medium text-slate-700">First name (optional)</span>
          <input
            type="text"
            {...register('firstName')}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
            placeholder="Alex"
            autoComplete="given-name"
          />
        </label>
        <label className="flex flex-col gap-2 text-left">
          <span className="text-sm font-medium text-slate-700">Last name (optional)</span>
          <input
            type="text"
            {...register('lastName')}
            className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
            placeholder="Johnson"
            autoComplete="family-name"
          />
        </label>
      </div>
      <button
        type="submit" 
        className="mt-8 inline-flex w-full items-center justify-center gap-2 btn-primary"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Saving ...' : 'Get insights'}
        <ArrowRightIcon className="h-5 w-5" />
      </button>
      <p className="mt-4 text-center text-xs text-slate-500">
        We&apos;ll share occasional FastBar updates, but you can unsubscribe anytime.
      </p>
    </form>
  );
};

export default EmailCaptureForm;
